import React, { Suspense, useMemo, useEffect, useState } from "react";
import PageContainer from "../../components/PageContainer/PageContainer";
import styled from "styled-components";
import Footer from "../../components/Footer";
import { barnCollectionData, animalCollectionData, flexItemsData, BIGCOINMAIN_ADDRESS, BIGCOINTOKEN_ADDRESS } from "../../utils/constants";
import PriceDisplay from "../../components/PriceDisplay/PriceDisplay";
import { AnimalCarousel } from "../../components/AnimalCarousel/AnimalCarousel";
import Button from "../../components/Button";
import { BarnCarousel } from "../../components/BarnCarousel/BarnCarousel";
import FlexShopCarousel from "../../components/FlexShopCarousel/FlexShopCarousel";
import BigcoinMainContractABI from "../../abis/bigcoinmain.abi.json";
import BigcoinTokenContractABI from "../../abis/bigcointoken.abi.json";
import ShadowPairABI from "../../abis/shadowpair.abi.json";

import HeroImage from "../../assets/img/heroimg2w.webp";
import "./homepage.css";
import { useReadContract } from "wagmi";
import { formatUnits, parseUnits } from "viem";
import { useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";

const HomeContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 4rem;
    max-width: 1400px;
    margin: 0 auto;
    gap: 4rem;

    @media (max-width: 1024px) {
        flex-direction: column;
        padding: 2rem;
    }
`;

const ContentSection = styled.div`
    flex: 1;
    max-width: 600px;
    text-align: left;

    @media (max-width: 1024px) {
        max-width: 100%;
        text-align: center;
    }
`;

const ImageSection = styled.div`
    flex: 1;
    display: flex;
    justify-content: flex-end;
    align-items: center;

    img {
        width: 600px;
        height: auto;
        object-fit: contain;
    }

    @media (max-width: 1024px) {
        img {
            width: 100%;
            max-width: 500px;
        }
    }
`;

const Title = styled.h1`
    font-size: 3em;
    line-height: 1.2;
    margin-bottom: 2rem;
    color: white;
    text-transform: uppercase;
    font-family: "Press Start 2P", monospace;
    letter-spacing: 2px;
    white-space: pre-line;
    text-align: left;

    @media (max-width: 1024px) {
        // font-size: 2.5em;
        text-align: center;
        white-space: normal;
    }
`;

const Subtitle = styled.p`
    font-size: 1.5em;
    margin-bottom: 3rem;
    color: white;
    font-family: "Press Start 2P", monospace;
    letter-spacing: 1px;
    line-height: 1.4;
    text-transform: uppercase;
    max-width: 500px;
    text-align: left;

    @media (max-width: 1024px) {
        // font-size: 2.5em;
        text-align: center;
        max-width: 100%;
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    gap: 1.5rem;

    @media (max-width: 1024px) {
        flex-direction: column;
        // align-items: stretch;
        // max-width: 250px;
        align-content: center;
        flex-wrap: wrap;
    }
`;

const LP_CREATED_TIMESTAMP = 1747000800 * 1000;
const LAUNCH_TIMESTAMP = 1747044000 * 1000;

const CountdownContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    
    margin: 24px 0 16px 0;
    @media (max-width: 600px) {
        gap: 6px;
        margin: 16px 0 8px 0;
    }
`;

const CountdownBox = styled.div`
    background: #1a120a;
    border: 2px solid #68949B;
    border-radius: 12px;
    padding: 10px 18px;
    min-width: 60px;
    text-align: center;
    margin: 0 2px;
    @media (max-width: 600px) {
        padding: 6px 8px;
        min-width: 36px;
        margin: 0 1px;
    }
`;

const CountdownNumber = styled.div`
    font-family: "Press Start 2P", monospace;
    font-size: 1.6rem;
    color: #fff;
    letter-spacing: 2px;
    @media (max-width: 600px) {
        font-size: 1rem;
    }
`;

const CountdownLabel = styled.div`
    font-family: "Press Start 2P", monospace;
    font-size: 0.8rem;
    color: #68949B;
    margin-top: 4px;
    letter-spacing: 1px;
    @media (max-width: 600px) {
        font-size: 0.6rem;
        margin-top: 2px;
    }
`;

const CountdownTitle = styled.div`
    font-family: "Press Start 2P", monospace;
    font-size: 1.1rem;
    color: #68949B;
    text-align: center;
    margin-bottom: 8px;
    letter-spacing: 1px;
    @media (max-width: 600px) {
        font-size: 0.8rem;
        margin-bottom: 4px;
    }
`;

function pad(n: number) {
    return n.toString().padStart(2, "0");
}

const MultiPhaseCountdown = () => {
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        const interval = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(interval);
    }, []);

    let target = LP_CREATED_TIMESTAMP;
    let label = "Time until Lp created";
    if (now >= LP_CREATED_TIMESTAMP && now < LAUNCH_TIMESTAMP) {
        target = LAUNCH_TIMESTAMP;
        label = "Time until game launch";
    }
    if (now >= LAUNCH_TIMESTAMP) {
        return (
            <div style={{ textAlign: "center", margin: "40px 0 24px 0" }}>
                <CountdownTitle>Game is live!</CountdownTitle>
            </div>
        );
    }

    const timeLeft = Math.max(0, target - now);
    const totalSeconds = Math.floor(timeLeft / 1000);
    // Remove days, only show hours, minutes, seconds
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return (
        <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", marginTop: "2rem" }}>
            <CountdownTitle>{label}</CountdownTitle>
            <CountdownContainer>
                <CountdownBox>
                    <CountdownNumber>{pad(hours)}</CountdownNumber>
                    <CountdownLabel>HOURS</CountdownLabel>
                </CountdownBox>
                <CountdownNumber style={{ color: "#68949B", fontSize: "1.6rem", margin: "0 4px" }}>:</CountdownNumber>
                <CountdownBox>
                    <CountdownNumber>{pad(minutes)}</CountdownNumber>
                    <CountdownLabel>MINUTES</CountdownLabel>
                </CountdownBox>
                <CountdownNumber style={{ color: "#68949B", fontSize: "1.6rem", margin: "0 4px" }}>:</CountdownNumber>
                <CountdownBox>
                    <CountdownNumber>{pad(seconds)}</CountdownNumber>
                    <CountdownLabel>SECONDS</CountdownLabel>
                </CountdownBox>
            </CountdownContainer>
        </div>
    );
};

const HomePage = () => {
    const { t, i18n } = useTranslation();

    const { data: currentMilkPrice } = useReadContract({
        address: "0x80797a61d219EAa4337eDCb1D44A54750bDADC9E" as any,
        abi: ShadowPairABI,
        functionName: 'getAmountOut',
        args: [parseUnits("1", 18), "0xA052cC4dD2138504Dd89E723D72636D491b4cEd9"]
    });

    const currentMilkPriceFormatted = useMemo(() => {
        if (currentMilkPrice) {
            return Number(formatUnits(currentMilkPrice as any ?? "0", 18).toString()).toFixed(2);
        }
        return "0.00";
    }, [currentMilkPrice]);

    const { data: uniqueMinerCount } = useReadContract({
        address: BIGCOINMAIN_ADDRESS,
        abi: BigcoinMainContractABI,
        functionName: 'uniqueMinerCount',
        args: []
    });

    const uniqueMinerCountFormatted = useMemo(() => {
        if (uniqueMinerCount) {
            return uniqueMinerCount.toString();
        }
        return "0";
    }, [uniqueMinerCount]);


    const { data: bigcoinTotalBurned } = useReadContract({
        abi: BigcoinTokenContractABI,
        address: BIGCOINTOKEN_ADDRESS,
        functionName: 'amtBurned',
        args: [],
        query: { refetchInterval: 1000 },
    });

    const bigcoinTotalBurnedFormatted = useMemo(() => {
        if (bigcoinTotalBurned) {
            return Number(formatUnits(bigcoinTotalBurned as any, 18)).toLocaleString("en-US", { maximumFractionDigits: 0 });
        }
        return "0";
    }, [bigcoinTotalBurned]);

    const { data: bigcoinTotalSupply } = useReadContract({
        address: BIGCOINTOKEN_ADDRESS,
        abi: BigcoinTokenContractABI,
        functionName: 'totalSupply',
        args: [],
        query: { refetchInterval: 1000 },
    });

    const bigcoinTotalSupplyFormatted = useMemo(() => {
        if (bigcoinTotalSupply) {
            return Number(formatUnits(bigcoinTotalSupply as any, 18)).toLocaleString("en-US", { maximumFractionDigits: 0 });
        }
        return "0";
    }, [bigcoinTotalSupply]);

    const isSmallScreen = useMediaQuery('(max-width: 1160px)');


    return (
        <PageContainer>
            <main style={{
                minHeight: '100vh',
                color: '#fff',
                fontFamily: "Press Start 2P, monospace"
            }}>
                <HomeContainer>
                    <ContentSection>
                        <Title>{t("home.hero.title")}</Title>
                        <Subtitle>
                            {t("home.hero.subtitle")}
                        </Subtitle>
                        <ButtonContainer>
                            <Button
                                variant="pixelAction"
                                size="xl"
                                shape="square"
                                className="min-w-[160px] px-5 py-3"
                                onClick={() => window.open("/farms", "_self")}
                            >{t("home.hero.action1")}</Button>
                            <Button
                                variant="pixelAction"
                                size="xl"
                                shape="square"
                                className="min-w-[200px] px-5 py-3"
                                onClick={() => window.open("https://docs.milkit.finance/", "_blank")}
                            >{t("home.hero.action2")}</Button>
                        </ButtonContainer>
                    </ContentSection>
                    <ImageSection>
                        <div style={{ position: "relative", width: 400, height: 400, margin: "0 auto" }}>
                            <img
                                src={HeroImage}
                                alt="landing gif"
                                style={{
                                    height: "100%"
                                    // position: "absolute",
                                    // bottom: 30,
                                    // left: 25,
                                    // width: 170,
                                    // zIndex: 2,
                                    // transform: "scaleX(-1)",
                                }}
                            />
                            {/* Donkey */}
                            {/* <img
                                src={donkeyImg}
                                alt="Donkey"
                                style={{
                                    position: "absolute",
                                    bottom: 30,
                                    left: 25,
                                    width: 170,
                                    zIndex: 2,
                                    transform: "scaleX(-1)",
                                }}
                            />
                            <img
                                src={cowImg}
                                alt="Cow"
                                style={{
                                    position: "absolute",
                                    bottom: 30,
                                    left: 130,
                                    width: 170,
                                    zIndex: 2,
                                }}
                            />
                            <img
                                src={acfarmerImg}
                                alt="AC Farmer"
                                style={{
                                    position: "absolute",
                                    bottom: 30,
                                    left: 260,
                                    width: 140,
                                    zIndex: 2,
                                    transform: "scaleX(-1)",
                                }}
                            />
                            <img
                                src={haybarnImg}
                                alt="Haybarn"
                                style={{
                                    position: "absolute",
                                    bottom: 0,
                                    left: 0,
                                    width: 400,
                                    zIndex: 1,
                                }}
                            /> */}
                        </div>
                    </ImageSection>
                </HomeContainer>

                <div className="container-xs mt-[124px] flex flex-col gap-[246px] lg:gap-[184px] lg:px-5 md:gap-[184px] md:px-5 sm:gap-[123px]" style={{ overflow: "hidden" }}>
                    <div className="mx-[38px] flex justify-center gap-6 md:mx-0 md:flex-wrap" style={{flexWrap: isSmallScreen ? "wrap" : "nowrap"}}>
                        <Suspense fallback={<div>Loading feed...</div>}>
                            {/* {financialOverviewList.map((d, index) => ( */}
                            <PriceDisplay currentPriceLabel={t("home.stats.stat_name1")} currentPriceValue={currentMilkPriceFormatted+" S"} />
                            <PriceDisplay currentPriceLabel={t("home.stats.stat_name2")} currentPriceValue={bigcoinTotalSupplyFormatted} />
                            <PriceDisplay currentPriceLabel={t("home.stats.stat_name3")} currentPriceValue={bigcoinTotalBurnedFormatted} />
                            <PriceDisplay currentPriceLabel={t("home.stats.stat_name4")} currentPriceValue={uniqueMinerCountFormatted} />
                            {/* // ))} */}
                        </Suspense>

                    </div>
                    <div className="flex flex-col items-center gap-[50px]" style={{ marginTop: "100px" }}>
                        <div className="w-full animal-carousel-container">
                            <Suspense fallback={<div>{t("home.carousel.loading.animal")}</div>}>
                                <AnimalCarousel animals={animalCollectionData} />
                            </Suspense>
                        </div>
                    </div>
                    <div className="flex flex-col items-center gap-[50px]" style={{ marginTop: "100px" }}>
                        <div className="w-full animal-carousel-container">
                            <Suspense fallback={<div>{t("home.carousel.loading.barn")}</div>}>
                                <BarnCarousel barns={barnCollectionData} />
                            </Suspense>
                        </div>
                    </div>
                    <div className="flex flex-col items-center gap-[50px]" style={{ marginTop: "100px", marginBottom: "100px" }}>
                        <div className="w-full animal-carousel-container">
                            <Suspense fallback={<div>{t("home.carousel.loading.flex")}</div>}>
                                <FlexShopCarousel flexItems={flexItemsData} />
                            </Suspense>
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </PageContainer>
    );
}

export default HomePage;
